import { createElement, useEffect, useMemo, useRef } from 'react';

import {
  CARE_STATE_SCOPE_LABEL,
  type ClinicalScopeT,
  useCareStateDomain,
} from '@/entities/care-state-domain';
import { useUserInfoQuery } from '@/entities/user-info';
import { useUserPhrQuery } from '@/entities/user-phr';
import {
  CARE_STATE_OVERVIEW_TAB_ID,
  CARE_STATE_SCOPED_TAB_ID,
  CareStateNoteTab,
} from '@/features/care-state-note-tab';
import type { FrontendPHRT } from '@lotushealth/lotushealth_js_sdk/schemas';
import { PatientMedicationsTab } from '@/features/patient-medications-tab';
import { PatientRxRequestsTab } from '@/features/patient-rx-requests-tab';
import { PhrTableTab } from '@/features/phr-table';
import { PatientPriorAuthsTab } from '@/features/prior-auth-tab';
import { setPatientPanelSelectedTab } from '@/shared/stores';

import type { PatientWorkspaceTab } from './types';
import { PatientLabsTab } from '@/features/patient-labs-tab';
import { LabsTabLabel } from '../ui/LabsTabLabel';

const PHR_TAB_KEYS = [
  'conditions',
  'labs',
  'medications',
  'notes',
  'procedures',
  'vitals',
] as const satisfies ReadonlyArray<keyof FrontendPHRT>;

function toTitleCase(value: string) {
  return value
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function usePatientWorkspaceTabs(userId: string | undefined) {
  const phrQuery = useUserPhrQuery(userId);
  const userInfoQuery = useUserInfoQuery(userId);
  const { scope } = useCareStateDomain();

  const insurance = userInfoQuery.data?.patientInfo?.insurance;
  const hasInsurance = insurance != null && insurance !== '';

  const scopeType = scope.type;
  const scopeId = scopeType === 'patient' ? null : scope.id;

  const scopeMatchedCareStateTabId =
    scopeType === 'patient' ? CARE_STATE_OVERVIEW_TAB_ID : CARE_STATE_SCOPED_TAB_ID;

  // Mirror scope transitions into the active tab. Skip the first render so
  // PatientPanel's tab fallback handles initial selection.
  const previousScopeTypeRef = useRef<ClinicalScopeT['type'] | null>(null);
  useEffect(() => {
    const previousScopeType = previousScopeTypeRef.current;
    previousScopeTypeRef.current = scopeType;

    if (previousScopeType === null) return;
    if (previousScopeType === scopeType) return;

    setPatientPanelSelectedTab(scopeMatchedCareStateTabId);
  }, [scopeType, scopeMatchedCareStateTabId]);

  // Stable scope references: tab content props change identity only on
  // semantic scope changes, not every parent render.
  const patientScope = useMemo<ClinicalScopeT>(() => ({ type: 'patient' }), []);
  const scopedScope = useMemo<ClinicalScopeT | null>(() => {
    if (scopeType === 'patient' || scopeId === null) return null;
    return scopeType === 'problem'
      ? { type: 'problem', id: scopeId }
      : { type: 'request', id: scopeId };
  }, [scopeType, scopeId]);

  const tabs = useMemo<PatientWorkspaceTab[]>(() => {
    const resolvedUserId = userId ?? '';

    const overviewTab: PatientWorkspaceTab = {
      id: CARE_STATE_OVERVIEW_TAB_ID,
      label: CARE_STATE_SCOPE_LABEL.patient,
      content: createElement(CareStateNoteTab, {
        patientUserId: resolvedUserId,
        scope: patientScope,
      }),
    };

    const scopedTab: PatientWorkspaceTab | null =
      scopedScope === null
        ? null
        : {
            id: CARE_STATE_SCOPED_TAB_ID,
            label: CARE_STATE_SCOPE_LABEL[scopedScope.type],
            content: createElement(CareStateNoteTab, {
              patientUserId: resolvedUserId,
              scope: scopedScope,
            }),
          };

    const careStateTabs: PatientWorkspaceTab[] =
      scopedTab === null ? [overviewTab] : [overviewTab, scopedTab];

    if (!userId) return careStateTabs;

    const composed: PatientWorkspaceTab[] = PHR_TAB_KEYS.map((key) => {
      if (key === 'labs') {
        return {
          id: 'phr:labs',
          label: createElement(LabsTabLabel, { hasInsurance }),
          content: createElement(PatientLabsTab, { userId: resolvedUserId }),
        };
      }
      if (key === 'medications') {
        return {
          id: 'phr:medications',
          label: toTitleCase('medications'),
          content: createElement(PatientMedicationsTab, { userId: resolvedUserId }),
        };
      }
      return {
        id: `phr:${key}`,
        label: toTitleCase(key),
        content: createElement(PhrTableTab, { userId: resolvedUserId, tableKey: String(key) }),
      };
    });

    const priorAuthsTab: PatientWorkspaceTab = {
      id: 'prior-auths',
      label: 'Prior Auths',
      content: createElement(PatientPriorAuthsTab, { userId: resolvedUserId }),
    };

    const rxInboxTab: PatientWorkspaceTab = {
      id: 'rx:inbox',
      label: 'Rx Inbox',
      content: createElement(PatientRxRequestsTab, { userId: resolvedUserId }),
    };

    return [...careStateTabs, ...composed, priorAuthsTab, rxInboxTab];
  }, [scopedScope, patientScope, userId, hasInsurance]);

  return { tabs, phrQuery };
}
